import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight } from 'lucide-react';

const TONES = [
  {
    id: 'friendly',
    label: 'Friendly',
    subject: 'Quick reminder about your invoice',
    greeting: 'Hey there,',
    body: 'Just checking in — I hope all is well! I wanted to kindly remind you about the outstanding invoice for £500. No rush, but when you get a chance, it would be great to get this sorted.',
    closing: 'Thanks so much!',
  },
  {
    id: 'professional',
    label: 'Professional',
    subject: 'Outstanding Invoice — Payment Due',
    greeting: 'Dear Client,',
    body: 'Following up on the outstanding invoice of £500 dated 1st March. As per our agreed terms, payment was due on receipt. Please arrange settlement at your earliest convenience.',
    closing: 'Kind regards,',
  },
  {
    id: 'firm',
    label: 'Firm',
    subject: 'Final Notice — Immediate Payment Required',
    greeting: 'To whom it may concern,',
    body: 'This is a final notice regarding the unpaid invoice of £500, now significantly overdue. If payment is not received within 7 days, we will be forced to escalate this matter further.',
    closing: 'Regards,',
  },
];

export default function ToneSwitchCard() {
  const [activeTone, setActiveTone] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTone(prev => (prev + 1) % 3);
        setIsTransitioning(false);
      }, 300);
    }, 4000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const handleToneClick = (index) => {
    clearInterval(intervalRef.current);
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveTone(index);
      setIsTransitioning(false);
    }, 200);
    intervalRef.current = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTone(prev => (prev + 1) % 3);
        setIsTransitioning(false);
      }, 300);
    }, 4000);
  };

  const tone = TONES[activeTone];

  return (
    <Link to="/email-generator" className="block" data-testid="feature-card-emails">
      <div className="h-full bg-[#0c0c12] border border-white/[0.06] rounded-xl p-5 sm:p-6 transition-all duration-300 hover:border-[#FF4500]/30 hover:shadow-[0_0_30px_-12px_rgba(255,69,0,0.15)] flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-[#FF4500]/10 flex items-center justify-center shrink-0">
            <Mail className="w-4 h-4 text-[#FF4500]" />
          </div>
          <h3 className="font-clash text-lg font-medium text-white">Payment Emails</h3>
        </div>

        {/* Tone buttons */}
        <div className="flex gap-1.5 mb-4">
          {TONES.map((t, i) => (
            <button
              key={t.id}
              onClick={(e) => { e.preventDefault(); handleToneClick(i); }}
              className={`px-3 py-1 rounded-full text-[10px] font-semibold transition-all duration-300 ${
                activeTone === i
                  ? 'bg-[#FF4500] text-white shadow-[0_0_12px_-2px_rgba(255,69,0,0.5)]'
                  : 'bg-white/[0.06] text-white/40 hover:bg-white/[0.1]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Email preview */}
        <div className="bg-black/40 border border-white/[0.06] rounded-lg p-3.5 flex-1 overflow-hidden">
          <div className={`transition-all duration-300 ${isTransitioning ? 'opacity-0 translate-y-1' : 'opacity-100 translate-y-0'}`}>
            <p className="text-[10px] text-white/30 mb-1">Subject:</p>
            <p className="text-[11px] text-white/80 font-medium mb-3">{tone.subject}</p>
            <p className="text-[10px] text-white/60 mb-1.5">{tone.greeting}</p>
            <p className="text-[10px] text-white/45 leading-relaxed mb-2 line-clamp-4">{tone.body}</p>
            <p className="text-[10px] text-white/60">{tone.closing}</p>
          </div>
        </div>

        <p className="text-xs text-white/30 italic mt-4 mb-3">Stop overthinking — send the message that works</p>
        <span className="inline-flex items-center text-sm font-medium text-[#FF4500] gap-1.5 group">
          Create Your Email <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}
